import { Fragment } from 'react';

import { Glow, Icon, LinkButton } from '@/components/ui';
import { paths } from '@/routes/paths';

import { HEADLINE_FROM, HEADLINE_TO, useScrollZoom, useSectionReveal } from './useSectionReveal';

/**
 * The headline, one word per mask.
 *
 * Split here rather than at runtime with a text-splitting plugin: the copy is
 * fixed, four words is not worth a dependency, and hand-splitting keeps the
 * accent colour attached to the words that carry it instead of to a range a
 * splitter would have to be told about.
 */
const HEADLINE = [
  { word: 'Wonder' },
  { word: 'Today,' },
  { word: 'Work', accent: true },
  { word: 'Tomorrow.', accent: true },
];

/**
 * The closing call to action.
 *
 * Keeps the `contact` id the footer links to, so the anchor in the footer's
 * "Contact" column still lands somewhere.
 *
 * `children` replaces the two default buttons for a page that closes on one
 * call rather than both -- give each button the `js-cta-button` class so it
 * joins the entrance.
 */
export function FinalCta({ children }) {
  const zoom = useScrollZoom();

  // Roughly two and a half seconds, on absolute positions rather than chained:
  //
  //   0.00  the two orbs sweep in from the edges and settle behind the copy
  //   0.15  the words come up out of their masks one at a time, out of focus
  //   1.15  the accent half blooms once as it lands, then settles
  //   1.30  the buttons overshoot into place
  //
  // The orbs are moved and scaled but never faded: `Glow` sets its own opacity
  // inline, and a reveal would finish by animating that to 1 -- a soft orb
  // turning into a flat disc.
  const root = useSectionReveal(({ timeline, select, reveal }) => {
    reveal('.js-cta-glow-l', { x: -160, scale: 0.4 }, { duration: 1.6, ease: 'power2.out' }, 0);
    reveal('.js-cta-glow-r', { x: 160, scale: 0.4 }, { duration: 1.6, ease: 'power2.out' }, 0);

    // Half the shared rise, with the word beat halved to match: four words on
    // the full three seconds and the 0.6s beat had the last one still arriving
    // five seconds in, which is a long time to hold a closing line.
    reveal(
      '.js-cta-word',
      HEADLINE_FROM,
      { ...HEADLINE_TO, duration: HEADLINE_TO.duration - 1.5, stagger: 0.3 },
      0.15,
    );

    // Not a `reveal`: text-shadow has no neutral to settle on, and the point is
    // a flare that comes and goes rather than a value that arrives. `yoyo`
    // spends the second half taking it back off.
    timeline.fromTo(
      select('.js-cta-accent'),
      { textShadow: '0 0 0 rgba(196, 176, 250, 0)' },
      {
        textShadow: '0 0 28px rgba(196, 176, 250, 0.6)',
        duration: 0.45,
        ease: 'sine.inOut',
        repeat: 1,
        yoyo: true,
      },
      1.15,
    );

    reveal(
      '.js-cta-button',
      { y: 34, scale: 0.8, opacity: 0, filter: 'blur(6px)' },
      { duration: 0.75, stagger: 0.12, ease: 'back.out(2.2)' },
      1.3,
    );
  });

  return (
    <section ref={root} id="contact" className="relative overflow-x-clip py-24 lg:py-32">
      <Glow className="js-cta-glow-l top-1/2 -left-40 -translate-y-1/2" size={620} opacity={0.16} />
      <Glow
        className="js-cta-glow-r top-1/2 -right-40 -translate-y-1/2"
        size={620}
        opacity={0.16}
      />

      <div ref={zoom} className="wonder-container relative text-center">
        {/* No scroll-away here: nothing follows this section but the footer,
            so there is no next section for the heading to hand over to. */}
        <h2 className="text-on-night text-[clamp(2.5rem,3.4vw,4rem)] leading-[1.08] font-semibold tracking-[-0.03em]">
          {HEADLINE.map(({ word, accent }, index) => (
            <Fragment key={word}>
              {/*
                One mask per word, `inline-block` so the words still lay out as
                a sentence and wrap where the line runs out. The padding and
                the negative margin that cancels it give the descenders room
                the mask would otherwise crop.
              */}
              <span className="inline-block overflow-hidden pb-[0.1em] mb-[-0.1em] align-bottom">
                <span
                  className={`js-cta-word inline-block ${
                    accent ? 'js-cta-accent text-iris-300' : ''
                  }`}
                >
                  {word}
                </span>
              </span>
              {/*
                The masks are inline-block with no whitespace between them in
                the markup, so without this the accessible name would come out
                as "WonderToday,WorkTomorrow.".
              */}
              {index < HEADLINE.length - 1 ? ' ' : null}
            </Fragment>
          ))}
        </h2>

        <div className="mt-10 flex flex-wrap justify-center gap-4">
          {children ?? <DefaultButtons />}
        </div>
      </div>
    </section>
  );
}

function DefaultButtons() {
  return (
    <>
      <LinkButton
        to={paths.registerStudent}
        variant="outlineLight"
        shape="pill"
        size="pillMd"
        className="js-cta-button"
      >
        <Icon name="student" size={17} />
        Let&rsquo;s Find My Internship
        <Icon name="arrowRight" size={16} />
      </LinkButton>
      <LinkButton
        to={paths.registerCompany}
        variant="light"
        shape="pill"
        size="pillMd"
        className="js-cta-button"
      >
        <Icon name="company" size={17} />
        Let&rsquo;s Hire An Intern
        <Icon name="arrowRight" size={16} />
      </LinkButton>
    </>
  );
}
